// src/pages/CarService.tsx
import { Link } from 'react-router-dom';
import { Wrench, Settings, Car, BarChart4, Thermometer, CheckCircle, ArrowRight, Star } from 'lucide-react';

const CarService = () => {
  const services = [
    {
      icon: <Car className="text-autoBlue text-4xl mb-4" />,
      title: 'Servis vseh znamk vozil',
      description: 'Redni in izredni servisi vseh znamk osebnih in dostavnih vozil. Uporabljamo kakovostne nadomestne dele in olja.'
    },
    {
      icon: <Wrench className="text-autoOrange text-4xl mb-4" />,
      title: 'Vzdrževanje in popravila',
      description: 'Popravila zavor, podvozja, izpušnih sistemov, motorjev in menjava obrabnih delov.'
    },
    {
      icon: <BarChart4 className="text-autoPurple text-4xl mb-4" />,
      title: 'Diagnostika vozil',
      description: 'Računalniška diagnostika napak za vse znamke vozil. Specializirani smo za elektronsko odkrivanje napak.'
    },
    {
      icon: <CheckCircle className="text-autoTeal text-4xl mb-4" />,
      title: 'Priprava na tehnični pregled',
      description: 'Temeljit pregled vozila in odprava morebitnih pomanjkljivosti pred tehničnim pregledom.'
    },
    {
      icon: <Thermometer className="text-autoRed text-4xl mb-4" />,
      title: 'Polnjenje klimatskih naprav',
      description: 'Servis in polnjenje klimatskih naprav, odpravljanje težav s hlajenjem in gretjem.'
    },
    {
      icon: <Settings className="text-autoYellow text-4xl mb-4" />,
      title: 'Dodatne storitve',
      description: 'Menjava pnevmatik, oljni servisi, menjava zobatih jermenov in verig, testiranje akumulatorjev.'
    }
  ];

  return (
    <div className="bg-gray-50">
      {/* Hero Section */}
      <section className="page-hero">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: "url('/assets/images/avtoservis/avtoservis-pagehero.jpg')" }}
        ></div>
        <div className="container mx-auto px-4 text-center relative z-10">
          <div className="hero-content max-w-3xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Avtoservis</h1>
            <p className="text-xl max-w-3xl mx-auto">Popravljamo in servisiramo vozila vseh znamk. Naši strokovnjaki poskrbijo za vaše vozilo s strokovno in kakovostno storitvijo.</p>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <h2 className="section-title text-center mb-12 after:left-1/2 after:-translate-x-1/2">Naše storitve</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div 
                key={index} 
                className="bg-white rounded-xl shadow-md p-6 hover:shadow-[0_0_15px_rgba(59,122,217,0.6)] hover:border-autoBlue transition-all duration-300 border border-gray-100 group card-glow"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="transition-transform duration-300 group-hover:scale-110">
                  {service.icon}
                </div>
                <h3 className="text-xl font-bold mb-2">{service.title}</h3>
                <p className="text-gray-600">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonial Section */}
      <section className="py-16 bg-autoGray">
        <div className="container mx-auto px-4">
          <h2 className="section-title text-center mb-12 after:left-1/2 after:-translate-x-1/2">Zadovoljne stranke</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white rounded-xl p-6 shadow-md hover:shadow-[0_0_15px_rgba(59,122,217,0.6)] hover:border-autoBlue transition-all duration-300 border border-gray-100 card-glow">
              <div className="flex items-center mb-4">
                <div className="flex">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-autoYellow fill-autoYellow animate-pulse" />
                  ))}
                </div>
              </div>
              <p className="text-gray-600 mb-4">"Hitro in zanesljivo! Odlična storitev, vse so uredili v dogovorjenem času. Moj avto deluje kot nov."</p>
              <p className="font-medium">- Janez Novak</p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-md hover:shadow-[0_0_15px_rgba(59,122,217,0.6)] hover:border-autoBlue transition-all duration-300 border border-gray-100 card-glow">
              <div className="flex items-center mb-4">
                <div className="flex">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-autoYellow fill-autoYellow animate-pulse" />
                  ))}
                </div>
              </div>
              <p className="text-gray-600 mb-4">"Profesionalno osebje, strokovna diagnostika in hitro odkrita napaka na avtomobilu. Toplo priporočam!"</p>
              <p className="font-medium">- Maja Kovač</p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-md hover:shadow-[0_0_15px_rgba(59,122,217,0.6)] hover:border-autoBlue transition-all duration-300 border border-gray-100 card-glow">
              <div className="flex items-center mb-4">
                <div className="flex">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-autoYellow fill-autoYellow animate-pulse" />
                  ))}
                </div>
              </div>
              <p className="text-gray-600 mb-4">"Že več let servisiram svoje vozilo pri njih in vedno sem zadovoljen. Zaupam njihovemu znanju in izkušnjam."</p>
              <p className="font-medium">- Luka Horvat</p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-md hover:shadow-[0_0_15px_rgba(59,122,217,0.6)] hover:border-autoBlue transition-all duration-300 border border-gray-100 card-glow">
              <div className="flex items-center mb-4">
                <div className="flex">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-autoYellow fill-autoYellow animate-pulse" />
                  ))}
                </div>
              </div>
              <p className="text-gray-600 mb-4">"Odlična izkušnja! Klimatska naprava spet deluje brezhibno, cena pa je bila zelo poštena."</p>
              <p className="font-medium">- Ana Šmid</p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <h2 className="section-title text-center mb-12 after:left-1/2 after:-translate-x-1/2">Zakaj izbrati nas?</h2>
          <div className="flex flex-col md:flex-row items-center gap-8">
            <div className="md:w-1/2 mb-8 md:mb-0">
              <img 
                src="/assets/images/avtoservis/zakaj-izbrati-nas.jpg" 
                alt="Diagnostična oprema" 
                className="rounded-xl shadow-md w-full h-[480px] object-cover transition-all duration-300 hover:shadow-[0_0_15px_rgba(59,122,217,0.6)] hover:border-autoBlue border border-gray-100 card-glow"
              />
            </div>
            <div className="md:w-1/2">
              <div className="bg-white rounded-xl shadow-md p-6 h-[480px] transition-all duration-300 hover:shadow-[0_0_15px_rgba(59,122,217,0.6)] hover:border-autoBlue border border-gray-100 card-glow">
                <ul className="grid grid-cols-1 gap-4">
                  <li className="flex items-start p-3 bg-autoGray rounded-lg transition-all duration-300 hover:bg-blue-50">
                    <CheckCircle className="text-autoBlue mr-3 mt-1 flex-shrink-0" size={20} />
                    <p>Več kot 25 let izkušenj v avtomobilski industriji</p>
                  </li>
                  <li className="flex items-start p-3 bg-autoGray rounded-lg transition-all duration-300 hover:bg-blue-50">
                    <CheckCircle className="text-autoBlue mr-3 mt-1 flex-shrink-0" size={20} />
                    <p>Najsodobnejša diagnostična oprema za vse znamke vozil</p>
                  </li>
                  <li className="flex items-start p-3 bg-autoGray rounded-lg transition-all duration-300 hover:bg-blue-50">
                    <CheckCircle className="text-autoBlue mr-3 mt-1 flex-shrink-0" size={20} />
                    <p>Uporaba originalnih in kakovostnih nadomestnih delov</p>
                  </li>
                  <li className="flex items-start p-3 bg-autoGray rounded-lg transition-all duration-300 hover:bg-blue-50">
                    <CheckCircle className="text-autoBlue mr-3 mt-1 flex-shrink-0" size={20} />
                    <p>Redno strokovno izobraževanje naših mehanikov</p>
                  </li>
                  <li className="flex items-start p-3 bg-autoGray rounded-lg transition-all duration-300 hover:bg-blue-50">
                    <CheckCircle className="text-autoBlue mr-3 mt-1 flex-shrink-0" size={20} />
                    <p>Poštene cene in transparentno poslovanje</p>
                  </li>
                  <li className="flex items-start p-3 bg-autoGray rounded-lg transition-all duration-300 hover:bg-blue-50">
                    <CheckCircle className="text-autoBlue mr-3 mt-1 flex-shrink-0" size={20} />
                    <p>Garancija na opravljene storitve</p>
                  </li>
                </ul>
              </div>
            </div>
          </div>
          <div className="mt-8 text-center">
            <Link to="/kontakt" className="btn-primary inline-flex items-center group">
              Kontaktirajte nas
              <ArrowRight className="ml-2 group-hover:ml-3 transition-all duration-300" size={20} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CarService;